// The Admin page's Agent shares tab (byj-1wv.49): which agents owners
// share in the pilot — agent shares opened, and agents their owners
// declined to share when asked to confirm what they reach — by what each
// agent can make happen, least first, over a period. Read only: the main
// server keeps them as byj-share and the relay say them.
import { useCallback, useEffect, useState } from "react";
import { api, type AgentShareReport, type ReachCount } from "../api";
import { t } from "../i18n";
import { EmptyRows, initialOf } from "./Rows";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export type AgentSharesApi = Pick<typeof api, "adminAgentShares">;

/** The periods offered, in days; 0 is since the first. */
const periods = [0, 30, 7] as const;

function ReachRow({ c }: { c: ReachCount }) {
  const quiet = c.opened === 0 && c.declined === 0;
  return (
    <li className={`share-row admin-row ${quiet ? "is-ended" : "is-live"}`} data-testid="agent-reach">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(c.reach || "?")}
      </span>
      <div className="row-main">
        <span className="row-title">{c.reach || t("admin:agentsUnknown")}</span>
        <div className="row-sub">{c.reach ? t(`agents:reach.${c.reach as "none" | "reads" | "hands-off" | "runs" | "writes" | "every-tool"}`) : t("admin:agentsUnknownSub")}</div>
      </div>
      <div className="row-meta">
        <span className="chip">{t("admin:agentsOpened", { count: c.opened })}</span>
        <span className={`chip${c.declined > 0 ? " chip-warn" : ""}`}>{t("admin:agentsDeclined", { count: c.declined })}</span>
        <span className="muted small">{t("admin:agentsOwners", { count: c.owners })}</span>
      </div>
    </li>
  );
}

export function AgentShares({ client = api, onCount }: { client?: AgentSharesApi; onCount?: (n: number) => void }) {
  const [days, setDays] = useState<number>(0);
  const [report, setReport] = useState<AgentShareReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const r = await client.adminAgentShares(days || undefined);
      setReport(r);
      setError(null);
      onCount?.(r.opened);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, days, onCount]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <div data-testid="admin-agents">
      <div className="panel-head">
        <p className="muted small panel-lead">{t("admin:agentsLead")}</p>
        <select className="small" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t("admin:agentsPeriod")}>
          {periods.map((d) => (
            <option key={d} value={d}>
              {d === 0 ? t("admin:agentsAll") : t("admin:agentsDays", { count: d })}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {report === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : report.opened + report.declined === 0 ? (
        <EmptyRows text={t("admin:agentsNone")} />
      ) : (
        <>
          <p className="muted small" role="status">
            {t("admin:agentsTotal", { opened: report.opened, declined: report.declined })}
          </p>
          <ul className="rows" aria-label={t("admin:adminTabAgents")}>
            {report.reaches.map((c) => (
              <ReachRow key={c.reach || "unknown"} c={c} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
